/**
 * `list-base-contracts` and `nominate-base-contract` — CLM-111 (FR-37, DR-11).
 * Refinements R-111-1 … R-111-9 are proposals made while developing; D-2.1-8,
 * D-2.1-9, D-2.1-11 and D-2.1-12, which they lean on, are recommendations, not
 * ratified decisions.
 *
 * An admin nominates one signed contract as cloudEQ's base MSA, and can
 * replace it. The base is an ordinary contract row; the nomination is a
 * separate, versioned decision about it (`clm.base_contract_nomination`,
 * 0101), so nominating changes nothing about how that contract behaves.
 * CLM-112's deviation report will store the `nomination_id` it ran against, so
 * a replaced base keeps every earlier report pinned to the version it was
 * computed from (DR-11).
 *
 * ## Modelled per type, enabled for MSA only (R-111-4)
 *
 * {@link BASE_CONTRACT_TYPES} is the switch. The table is keyed per
 * `contract_type` with no CHECK narrowing it (D-2.1-12), so enabling NDA later
 * is one line here and no migration. Both endpoints read the tuple: the list
 * answers one entry per enabled type, and the nominate request refuses any
 * other type at the schema (a 400 before a statement runs).
 *
 * ## Eligibility (R-111-5)
 *
 * {@link baseContractIneligibility} is the one definition, used by the API to
 * refuse and by the contract record to decide whether to offer the action at
 * all. A base must be a **signed** contract of the nomination's own type
 * (D-2.1-8 — never a blank template, never an unsigned `Draft`), not an
 * amendment, not `Superseded` (nominate the agreement that replaced it), and
 * not `processing_restricted` (§4C keeps a restricted contract from the model,
 * so its clauses are only partly matched and CLM-112 would silently compare
 * less). `Expired` and `Terminated` are admitted: the base's terms can still be
 * the company position after its own term ends.
 *
 * **An amendment is refused by its edge, not by its type.** The guided upload
 * files an amendment under its parent's type (`apps/api/src/upload/derive.ts`
 * offers `amends` only when the two types match), so an MSA amendment is
 * stored as `contract_type = 'MSA'` with `amends_contract_key` set; the
 * provisional `AMENDMENT` type is not what intake writes. A type test alone
 * would promote a document that holds only the changes to an MSA, so
 * `amends_contract_key IS NULL` is part of the rule. (Since CLM-110's decision
 * migration the schema holds the type half: `fk_contract_amends_type` makes an
 * amendment carry its agreement's type, and `AMENDMENT` is unstorable.)
 *
 * **A base must have a confirmed type (CLM-110, R-110-21).** A provisional type
 * is the file name's guess or a person's hint, and a base whose type is wrong
 * corrupts every later deviation report. The nominator is an Admin, who can
 * confirm the type first; `type_provisional` is checked right after
 * `wrong_contract_type`, and the API's `INSERT … SELECT` restates it
 * (`contract_type_confirmation = 'confirmed'`, under `FOR SHARE`). Existing
 * nominations are untouched.
 *
 * **Admin-confirmed, not merely settled (CLM-118, R-118-13, B26).** A type
 * confirmed because the file name and the AI agree (`auto_confirmed`) was
 * checked by no person, so it is `type_not_admin_confirmed`: the web offers
 * "Confirm type and set as base", which posts the Admin's decision and then the
 * nomination — two audited acts, each by its own route.
 *
 * ## Replacing, and two admins at once (R-111-3)
 *
 * The request carries `expected_current_nomination_id` — the current
 * nomination the caller saw, `null` when there was none. It is required, never
 * optional, so a client cannot replace a base it did not look at. A mismatch
 * is `409 base_changed`; so is losing the race to another admin, which the
 * schema's unique indexes decide (FR-37: *"uniqueness enforced by the schema"*).
 *
 * ## Gates (R-111-6) and scope (R-111-7)
 *
 * Nominate is `admin:configure` (FR-37 names it; Admin only). The list is
 * `clause:review_match` — Admin **and** Legal, per D-2.1-11 — so Legal sees the
 * base read-only. Every statement over the table joins `vw_contract_scope` on
 * the nominated contract and predicates on `effective_contract_key` (rules 9,
 * 10): a caller sees only nominations whose contract they may see, and
 * `scope_kind` says when that is part of the corpus. There is no scope-view
 * change (Q-111-1 is CLM-112's).
 */
import { z } from 'zod';
import { ContractKeySchema, IsoTimestampUtcSchema, OpaqueIdSchema, UpnSchema, bigintIdTextSchema } from '../primitives.js';
import { LifecycleStatusSchema } from '../vocabulary.js';
/**
 * The contract types a base may be nominated for — D-2.1-12, MSA only.
 *
 * ⚑ **Recommendation, not ratified.** Adding `'NDA'` here is the whole of
 * enabling a base NDA: the list grows an entry and the nominate request
 * admits it. `clm.base_contract_nomination` already allows it.
 */
export const BASE_CONTRACT_TYPES = ['MSA'];
export const BaseContractTypeSchema = z.enum(BASE_CONTRACT_TYPES);
/**
 * `clm.base_contract_nomination.nomination_id` — a `bigint` identity, carried
 * as a decimal string: `pg` returns `int8` as a string, and a JavaScript number
 * would silently lose precision above 2^53. Bounded by the `int8` range
 * (`bigintIdTextSchema`): an id past it is a `400` at the schema, never a
 * 22003 at the `::bigint` cast, which the caller would see as a `500`.
 * CLM-112's run body reuses this schema.
 */
export const NominationIdSchema = bigintIdTextSchema('expected a nomination id');
/** The admin's optional reason, as `ck_base_contract_nomination_note` bounds it: 1..500, never blank. */
export const BaseContractNoteSchema = z
    .string()
    .min(1)
    .max(500)
    .refine((value) => value.trim().length > 0, 'Must contain a non-whitespace character');
/* -------------------------------------------------------------------------- */
/* Eligibility — one definition for the API and the contract record            */
/* -------------------------------------------------------------------------- */
/** Why a contract cannot be the base (R-111-5). Each is a `field_errors` code on a 400. */
export const BASE_CONTRACT_INELIGIBILITY_REASONS = [
    /** The contract's type is not the nomination's type. */
    'wrong_contract_type',
    /** CLM-110 (R-110-21) — the type is not settled yet (provisional, or Type pending): confirm it first. */
    'type_provisional',
    /** `amends_contract_key` is set — it holds only changes; nominate the agreement it amends. */
    'amendment',
    /** `Draft` — unsigned. D-2.1-8: a signed MSA is promoted, never a template. */
    'not_signed',
    /** `Superseded` — nominate the agreement that replaced it. */
    'superseded',
    /** §4C — no model has seen its clauses, so CLM-112 would compare less without saying so. */
    'processing_restricted',
    /**
     * CLM-118 (R-118-13) — settled because the file name and the AI agree
     * (`auto_confirmed`), but no Admin has confirmed it: confirm the type, then set
     * the base. Appended at the end (enum order is API surface).
     */
    'type_not_admin_confirmed',
];
/** A `retrieve-contract` detail as a candidate — the preview and the contract record both hold one. */
export function baseContractCandidateOf(detail) {
    return {
        contract_type: detail.contract_type,
        contract_type_confirmation: detail.contract_type_status.confirmation,
        amends_contract_key: detail.lineage.amends_contract_key,
        lifecycle_status: detail.lifecycle_status,
        processing_restricted: detail.processing_restricted,
    };
}
/** The lifecycle states {@link baseContractIneligibility} refuses, as literals the API's SQL restates. */
export const BASE_CONTRACT_REFUSED_LIFECYCLE = Object.freeze(['Draft', 'Superseded']);
/**
 * The first reason `contract` cannot be nominated as the `type` base, or
 * `null` when it can. Checked in this order so the most basic mismatch is the
 * one reported. Pure: the API refuses on it and the contract record decides
 * whether to offer the action with it, so the two cannot disagree. The API's
 * INSERT restates the same predicate, so the database — not this function — is
 * what makes an ineligible nomination impossible.
 */
export function baseContractIneligibility(type, contract) {
    if (contract.contract_type !== type)
        return 'wrong_contract_type';
    if (contract.contract_type_confirmation === 'auto_confirmed')
        return 'type_not_admin_confirmed';
    if (contract.contract_type_confirmation !== 'confirmed')
        return 'type_provisional';
    if (contract.amends_contract_key !== null)
        return 'amendment';
    if (contract.lifecycle_status === 'Draft')
        return 'not_signed';
    if (contract.lifecycle_status === 'Superseded')
        return 'superseded';
    if (contract.processing_restricted)
        return 'processing_restricted';
    return null;
}
/* -------------------------------------------------------------------------- */
/* One nomination                                                              */
/* -------------------------------------------------------------------------- */
/**
 * One row of `clm.base_contract_nomination`, with three facts about its
 * contract read live: `contract_title`, `lifecycle_status` and
 * `type_mismatch`.
 *
 * `contract_title` is the primary party's cached display name — `clm.contract`
 * has no title column, and it is the name the contract record and the search
 * hits already lead with. `null` only when the contract has no primary party
 * row. `type_mismatch` is R-111-9: the contract's type was corrected after it
 * was nominated; the nomination stays and says so.
 */
export const BaseContractNominationSchema = z
    .object({
    nomination_id: NominationIdSchema,
    /** 1-based, per contract type. */
    version_no: z.number().int().min(1),
    /** The version this one replaced; `null` exactly for version 1. */
    supersedes_nomination_id: NominationIdSchema.nullable(),
    contract_key: ContractKeySchema,
    contract_title: z.string().min(1).max(200).nullable(),
    /** The contract's lifecycle **now**, not at nomination — a base can later expire or be superseded. */
    lifecycle_status: LifecycleStatusSchema,
    nominated_by_upn: UpnSchema,
    nominated_utc: IsoTimestampUtcSchema,
    note: BaseContractNoteSchema.nullable(),
    /** Set when a later nomination replaced this one; both or neither. */
    superseded_utc: IsoTimestampUtcSchema.nullable(),
    superseded_by_upn: UpnSchema.nullable(),
    /** R-111-9 — the contract's type no longer equals the nomination's. Flagged, never enforced. */
    type_mismatch: z.boolean(),
})
    .strict()
    .superRefine((row, ctx) => {
    if ((row.version_no === 1) !== (row.supersedes_nomination_id === null)) {
        ctx.addIssue({
            code: 'custom',
            path: ['supersedes_nomination_id'],
            message: 'version 1 has no predecessor and every later version has one',
        });
    }
    if ((row.superseded_utc === null) !== (row.superseded_by_upn === null)) {
        ctx.addIssue({
            code: 'custom',
            path: ['superseded_by_upn'],
            message: 'superseded_utc and superseded_by_upn are set together or not at all',
        });
    }
});
/* -------------------------------------------------------------------------- */
/* list-base-contracts                                                         */
/* -------------------------------------------------------------------------- */
/** No parameters. `.strict()`, so a `scope` or `contract_type` key is a 400, never ignored. */
export const ListBaseContractsRequestSchema = z.object({}).strict();
/**
 * One enabled type: its current base and every earlier version, newest first.
 *
 * `history` holds **superseded** versions only, so a nomination appears
 * exactly once in an entry. Both halves are under the caller's scope: a
 * practices caller who cannot see the current base's contract reads
 * `current: null` although a base exists — which `scope_kind` lets the page say.
 */
export const BaseContractTypeEntrySchema = z
    .object({
    contract_type: BaseContractTypeSchema,
    current: BaseContractNominationSchema.nullable(),
    history: z.array(BaseContractNominationSchema),
})
    .strict()
    .superRefine((entry, ctx) => {
    if (entry.current !== null && entry.current.superseded_utc !== null) {
        ctx.addIssue({ code: 'custom', path: ['current'], message: 'the current nomination is never superseded' });
    }
    entry.history.forEach((row, index) => {
        if (row.superseded_utc === null) {
            ctx.addIssue({ code: 'custom', path: ['history', index], message: 'history holds superseded versions only' });
        }
        const newer = index === 0 ? entry.current : entry.history[index - 1];
        if (newer !== null && newer !== undefined && newer.version_no <= row.version_no) {
            ctx.addIssue({ code: 'custom', path: ['history', index], message: 'history is newest first, below the current version' });
        }
    });
});
export const ListBaseContractsResponseSchema = z
    .object({
    /**
     * The caller's scope shape, never the practice list. `practices` means only
     * nominations whose contract is in the caller's scope are listed (R-111-7).
     */
    scope_kind: z.enum(['all', 'practices']),
    /** One entry per {@link BASE_CONTRACT_TYPES}, in that order — an enabled type with no base reads `current: null`. */
    types: z.array(BaseContractTypeEntrySchema),
})
    .strict()
    .superRefine((body, ctx) => {
    const listed = body.types.map((entry) => entry.contract_type);
    if (listed.length !== BASE_CONTRACT_TYPES.length || listed.some((type, index) => type !== BASE_CONTRACT_TYPES[index])) {
        ctx.addIssue({
            code: 'custom',
            path: ['types'],
            message: `types must be exactly ${BASE_CONTRACT_TYPES.join(', ')}, in that order`,
        });
    }
});
/* -------------------------------------------------------------------------- */
/* nominate-base-contract                                                      */
/* -------------------------------------------------------------------------- */
/**
 * Nominate `contract_key` as the `contract_type` base — the first one, or a
 * replacement. The same request does both: `expected_current_nomination_id`
 * is what distinguishes them, and what makes a stale page a 409 rather than a
 * silent overwrite.
 */
export const NominateBaseContractRequestSchema = z
    .object({
    contract_type: BaseContractTypeSchema,
    contract_key: ContractKeySchema,
    /** The current nomination the caller saw, or `null` for "there is none". Required — never inferred. */
    expected_current_nomination_id: NominationIdSchema.nullable(),
    /** Why this contract is the company position. Stored on the nomination and shown with it. */
    note: BaseContractNoteSchema.optional(),
})
    .strict();
/**
 * The new current nomination and the ids of the `0046` audit rows the write
 * produced, in write order: one on a first nomination (`Create`), two on a
 * replacement (`Update` on the superseded row, then `Create`) — R-111-8. Each
 * row is on its own contract's trail.
 */
export const NominateBaseContractResponseSchema = z
    .object({
    nomination: BaseContractNominationSchema,
    audit_ids: z.array(OpaqueIdSchema).min(1).max(2),
})
    .strict()
    .superRefine((body, ctx) => {
    const expected = body.nomination.supersedes_nomination_id === null ? 1 : 2;
    if (body.audit_ids.length !== expected) {
        ctx.addIssue({
            code: 'custom',
            path: ['audit_ids'],
            message: 'a first nomination writes one audit row and a replacement two',
        });
    }
    if (body.nomination.superseded_utc !== null) {
        ctx.addIssue({ code: 'custom', path: ['nomination'], message: 'a nomination just made is current' });
    }
});
//# sourceMappingURL=base-contracts.js.map